import React, { useEffect, useMemo, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import ProfileHeader from '../components/ProfileHeader';
import DrillTemplateEditorForm from '../components/DrillTemplateEditorForm';
import { loadUserOrganizationData } from '../utils/organizationData';
import { useSelectedOrganizationContext } from '../utils/organizationSelection';
import { loadAccessibleDrillTemplates } from '../utils/drillTemplateAccess';
import { createDrillTemplate, updateDrillTemplate } from '../utils/drillTemplateWrites';
import {
  canAccessDrillTemplateManagement,
  canAttachContentPack,
  canEditContentItem,
  canManageContentPack,
  canManageOrganizationContent,
} from '../utils/platformAccess';
import { normalizeEntitlementRecord } from '../utils/contentPackAccess';
import { loadScopedDrillTemplates } from '../utils/drillTemplateQueries';
import { loadAllContentPacks } from '../utils/contentPackQueries';

function buildDefaultForm(selectedOrganizationId = '', currentUserId = '') {
  return {
    title: '',
    description: '',
    summary: '',
    sport: '',
    discipline: '',
    topic: '',
    commonError: '',
    correctionCue: '',
    tags: [],
    focusAreas: [],
    levels: [],
    steps: [''],
    relatedSkillIds: [],
    visibility: 'private',
    accessTier: 'free',
    ownerType: 'instructor',
    organizationId: selectedOrganizationId,
    ownerId: currentUserId,
    contentPackId: '',
    assignedUserIds: [],
  };
}

function DrillTemplateManagementPage() {
  const navigate = useNavigate();
  const { templateId: routeTemplateId = '' } = useParams();
  const [searchParams] = useSearchParams();
  const { currentUser, userData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [contentPacks, setContentPacks] = useState([]);
  const [entitlements, setEntitlements] = useState([]);
  const [organizationMemberships, setOrganizationMemberships] = useState([]);
  const [organizationsById, setOrganizationsById] = useState({});
  const [filter, setFilter] = useState('all');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [form, setForm] = useState(buildDefaultForm());

  const templateId = routeTemplateId || searchParams.get('templateId') || '';
  const mode = routeTemplateId || templateId ? 'edit' : 'create';

  const { memberships, selectedOrganization, selectedOrganizationId, setSelectedOrganizationId } =
    useSelectedOrganizationContext(userData, {
      organizationMembers: organizationMemberships,
      organizationsById,
    });

  const currentOrganizationIds = memberships.map((membership) => membership.organizationId);
  const canCreatePlatformContent = canManageContentPack(userData);
  const canManageSelectedOrganizationContent = canManageOrganizationContent(
    { ...userData, organizationMemberships: memberships },
    selectedOrganizationId
  );
  const canAttachSelectedOrganizationPacks = canAttachContentPack(
    { ...userData, organizationMemberships: memberships },
    selectedOrganizationId
  );

  useEffect(() => {
    let cancelled = false;

    async function loadData() {
      setLoading(true);
      try {
        const organizationData = await loadUserOrganizationData(db, {
          currentUser,
          userData,
        });
        const [contentPackRecords, entitlementSnap] = await Promise.all([
          loadAllContentPacks(db),
          getDocs(collection(db, 'entitlements')),
        ]);
        const normalizedEntitlements = entitlementSnap.docs.map((docSnap) =>
          normalizeEntitlementRecord({ id: docSnap.id, ...docSnap.data() })
        );
        const scopedTemplates = await loadScopedDrillTemplates(db, {
          currentUserId: currentUser?.uid || '',
          organizationIds: organizationData.memberships.map((membership) => membership.organizationId),
          currentUserOrgId: organizationData.memberships[0]?.organizationId || '',
        });
        const accessibleTemplates =
          scopedTemplates.length > 0
            ? scopedTemplates
            : await loadAccessibleDrillTemplates(db, {
          currentUser,
          userData,
          organizationMemberships: organizationData.memberships,
          entitlements: normalizedEntitlements,
        });

        if (cancelled) return;

        setOrganizationMemberships(organizationData.memberships);
        setOrganizationsById(organizationData.organizationsById);
        setContentPacks(contentPackRecords);
        setEntitlements(normalizedEntitlements);
        setTemplates(accessibleTemplates);
      } catch (loadError) {
        console.error(loadError);
        if (!cancelled) {
          setError('Could not load drill templates right now.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadData();

    return () => {
      cancelled = true;
    };
  }, [currentUser, userData]);

  const selectedTemplate = useMemo(
    () => templates.find((template) => template.id === templateId) || null,
    [templateId, templates]
  );

  useEffect(() => {
    if (mode === 'edit' && selectedTemplate) {
      setForm({
        title: selectedTemplate.title || '',
        description: selectedTemplate.description || '',
        summary: selectedTemplate.summary || selectedTemplate.description || '',
        sport: selectedTemplate.sport || '',
        discipline: selectedTemplate.discipline || '',
        topic: selectedTemplate.topic || '',
        commonError: selectedTemplate.commonError || '',
        correctionCue: selectedTemplate.correctionCue || '',
        tags: selectedTemplate.tags || [],
        focusAreas: selectedTemplate.focusAreas || [],
        levels: selectedTemplate.level || [],
        steps: selectedTemplate.steps?.length ? selectedTemplate.steps : [''],
        relatedSkillIds: (selectedTemplate.relatedSkillIds || [])
          .map((skillRef) => (typeof skillRef === 'string' ? skillRef : skillRef.skillId))
          .filter(Boolean),
        visibility: selectedTemplate.accessVisibility || selectedTemplate.visibility || 'private',
        accessTier: selectedTemplate.accessTier || 'free',
        ownerType: selectedTemplate.ownerType || 'instructor',
        organizationId: selectedTemplate.organizationId || selectedTemplate.orgId || selectedOrganizationId,
        ownerId: selectedTemplate.ownerId || selectedTemplate.createdBy || currentUser?.uid || '',
        contentPackId: selectedTemplate.contentPackId || '',
        assignedUserIds: selectedTemplate.assignedUserIds || [],
      });
      return;
    }

    if (mode === 'create') {
      setForm(buildDefaultForm(selectedOrganizationId, currentUser?.uid || ''));
    }
  }, [currentUser?.uid, mode, selectedOrganizationId, selectedTemplate]);

  const filteredTemplates = useMemo(() => {
    return templates.filter((template) => {
      if (filter === 'mine') {
        return template.ownerType === 'instructor'
          && (template.createdBy === currentUser?.uid || template.ownerId === currentUser?.uid);
      }
      if (filter === 'organization') return template.ownerType === 'organization';
      if (filter === 'platform') return template.ownerType === 'platform';
      if (filter === 'premium') return template.accessTier === 'premium' || Boolean(template.contentPackId);
      return true;
    });
  }, [currentUser?.uid, filter, templates]);

  const editableTemplates = useMemo(() => {
    return filteredTemplates.filter((template) =>
      canEditContentItem(template, {
        currentUserId: currentUser?.uid || '',
        currentOrganizationIds,
        userData: { ...userData, organizationMemberships: memberships },
      })
    );
  }, [currentOrganizationIds, currentUser?.uid, filteredTemplates, memberships, userData]);

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    setError('');

    const payload = {
      ...form,
      description: form.description || form.summary,
      summary: form.summary || form.description,
      tags: (form.tags || []).map((item) => item.trim()).filter(Boolean),
      focusAreas: (form.focusAreas || []).map((item) => item.trim()).filter(Boolean),
      level: (form.levels || []).map((item) => item.trim()).filter(Boolean),
      steps: (form.steps || []).map((item) => item.trim()).filter(Boolean),
      relatedSkillIds: (form.relatedSkillIds || [])
        .map((item) => item.trim())
        .filter(Boolean)
        .map((skillId) => ({ skillId, treeId: 'sas_ski_tree_v1' })),
      assignedUserIds: (form.assignedUserIds || []).map((item) => item.trim()).filter(Boolean),
    };

    try {
      if (mode === 'edit' && selectedTemplate) {
        await updateDrillTemplate(
          db,
          selectedTemplate.id,
          {
            ...selectedTemplate,
            ...payload,
          },
          {
            currentUser,
            userData: { ...userData, organizationMemberships: memberships },
            organizationId: selectedOrganizationId,
          }
        );
        setMessage('Drill template updated.');
      } else {
        const created = await createDrillTemplate(
          db,
          payload,
          {
            currentUser,
            userData: { ...userData, organizationMemberships: memberships },
            organizationId: selectedOrganizationId,
          }
        );
        setMessage('Drill template created.');
        navigate(`/content/drills/${created.id}/edit`);
      }
    } catch (saveError) {
      console.error(saveError);
      setError(`Could not ${mode === 'edit' ? 'update' : 'create'} the drill template.`);
    } finally {
      setSaving(false);
    }
  };

  const accessiblePackOptions = contentPacks.filter((contentPack) => {
    if (!canAttachSelectedOrganizationPacks) {
      return Boolean(form.contentPackId) && contentPack.contentPackId === form.contentPackId;
    }
    if (!contentPack.isPremium) return true;
    if (canCreatePlatformContent) return true;
    return entitlements.some(
      (entitlement) =>
        entitlement.organizationId === selectedOrganizationId &&
        entitlement.contentPackId === contentPack.contentPackId &&
        entitlement.status === 'active'
    );
  });

  const ownerTypeOptions = [
    { value: 'instructor', label: 'My private content' },
    ...(canManageSelectedOrganizationContent
      ? [{ value: 'organization', label: 'Organization content' }]
      : []),
    ...(canCreatePlatformContent ? [{ value: 'platform', label: 'Platform content' }] : []),
  ];

  return (
    <div className="mx-auto max-w-7xl p-4">
      <ProfileHeader />
      <section className="mt-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Content</p>
            <h1 className="mt-2 text-2xl font-semibold text-slate-950">Drill Template Management</h1>
            <p className="mt-2 max-w-3xl text-sm text-slate-600">
              Create, edit, and govern private, organization, platform, and premium drill templates from one workspace.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/content/drills/new" className="rounded-xl bg-slate-900 px-4 py-2 text-sm font-semibold text-white">
              New Drill Template
            </Link>
            <Link to="/organization" className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700">
              Back to Organization
            </Link>
          </div>
        </div>

        {memberships.length > 1 ? (
          <div className="mt-4 max-w-xs">
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Organization Context</label>
            <select
              value={selectedOrganizationId}
              onChange={(e) => setSelectedOrganizationId(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
            >
              {memberships.map((membership) => (
                <option key={membership.organizationId} value={membership.organizationId}>
                  {membership.organizationName}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        {message ? <p className="mt-4 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-700">{message}</p> : null}
        {error ? <p className="mt-4 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700">{error}</p> : null}

        {!canAccessDrillTemplateManagement({ ...userData, organizationMemberships: memberships }) ? (
          <p className="mt-4 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700">
            Your account can view this workspace route only after instructor or organization content permissions are available.
          </p>
        ) : null}

        <div className="mt-6 grid gap-4 lg:grid-cols-[1.1fr,0.9fr]">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h2 className="text-lg font-semibold text-slate-950">Accessible Drill Templates</h2>
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                className="rounded-xl border border-slate-200 px-3 py-2 text-sm"
              >
                <option value="all">All accessible drills</option>
                <option value="mine">My Private Drills</option>
                <option value="organization">Organization Drills</option>
                <option value="platform">Platform Drills</option>
                <option value="premium">Premium Drills</option>
              </select>
            </div>

            {loading ? (
              <p className="mt-4 text-sm text-slate-500">Loading drill templates...</p>
            ) : (
              <div className="mt-4 space-y-3">
                {editableTemplates.map((template) => (
                  <button
                    key={template.id}
                    type="button"
                    onClick={() => navigate(`/content/drills/${template.id}/edit`)}
                    className={`w-full rounded-2xl border px-4 py-3 text-left text-sm ${
                      template.id === selectedTemplate?.id
                        ? 'border-slate-900 bg-slate-100'
                        : 'border-slate-200 bg-white'
                    }`}
                  >
                    <div className="font-semibold text-slate-900">{template.title || 'Untitled Drill'}</div>
                    <div className="mt-1 text-slate-600">{template.topic || template.sport || 'General content item'}</div>
                    <div className="mt-2 text-xs text-slate-500">
                      {(template.ownerType || 'instructor')} · {(template.accessVisibility || template.visibility || 'private')} · {template.accessTier || 'free'}
                    </div>
                  </button>
                ))}
                {!loading && editableTemplates.length === 0 ? (
                  <p className="text-sm text-slate-500">No editable drill templates matched this filter yet.</p>
                ) : null}
              </div>
            )}
          </div>

          <div>
            <DrillTemplateEditorForm
              form={form}
              onChange={(field, value) => setForm((current) => ({ ...current, [field]: value }))}
              ownerTypeOptions={ownerTypeOptions}
              accessiblePackOptions={accessiblePackOptions}
              memberships={memberships}
              selectedOrganizationId={selectedOrganizationId}
              onSelectedOrganizationIdChange={setSelectedOrganizationId}
              onSubmit={handleSave}
              saving={saving}
              mode={mode}
            />

            <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm text-slate-600">
              <div><strong>Current org:</strong> {selectedOrganization?.organizationName || 'No organization selected'}</div>
              <div className="mt-1"><strong>Premium packs available:</strong> {accessiblePackOptions.length}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

export default DrillTemplateManagementPage;
